import {
  blockFromClick,
  BlockStatus,
  MINUTES_PER_DAY,
  ResizeEdge,
  type CreateOptions,
  type GroupResizeResult,
  type IdSpan,
  type LiveTimeBlock,
  type MarkOutcome,
  type Span,
} from '@wiztime/core';
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { AnchorRect } from '../dayUiStore';
import type { GestureKind } from '../gestureCount';
import { GestureKind as Gestures } from '../gestureCount';
import { edgeScrollVelocity, scrollStepPx } from './autoScroll';
import {
  cardHeightPx,
  edgeFromBands,
  memberForEdge,
  minuteAtY,
  outerResizeTarget,
  type LaneGeometry,
  type OuterResizeTarget,
} from './laneHitTest';
import { MIN_TEXT_BLOCK_PX, type DisplayGroup } from './laneModel';
import { beginQuickMarkWrite, endQuickMarkWrite } from './quickMarkGuard';
import { horizontalScrollContainerOf, scrollContainerOf } from './scrollContainers';
import {
  computePreview,
  createPreview,
  Gesture,
  type LanePreview,
  PreviewKind,
  samePreview,
  sameSpan,
  spanOf,
} from './lanePreview';

/**
 * Pointer state machine of a block lane (ui-redesign §3.2, §3.3): click and drag creation,
 * whole-block and whole-group moving, edge resizing with flip, interruption handling, auto-
 * scroll, and — in the week view — the cross-day move across the grid's day columns (§2.8).
 * The store is written once, on release; every interruption discards the preview.
 */

export { PreviewKind, type LanePreview } from './lanePreview';

/** Viewport position of the pointer while a drag is in progress; anchors the floating tip. */
export interface DragPoint {
  readonly x: number;
  readonly y: number;
  /** A touch drag puts the tip above the finger instead of beside the cursor. */
  readonly touch: boolean;
}

/** Movement below this many pixels is a click, not a drag. */
export const DRAG_THRESHOLD_PX = 4;
/** A touch that moves farther than this before the long press is a scroll. */
const TOUCH_SLOP_PX = 10;
/** Touch drags on a block or empty area arm after pressing this long (ui-redesign §3.3). */
export const LONG_PRESS_MS = 380;
export { AUTO_SCROLL_EDGE_PX, AUTO_SCROLL_MAX_PX_PER_S } from './autoScroll';
/** Visual arming of a touch long press: the next move drags instead of scrolling (§3.3). */
export const ArmedKind = { Create: 'create', Move: 'move' } as const;
export type ArmedKind = (typeof ArmedKind)[keyof typeof ArmedKind];

interface Session {
  readonly pointerId: number;
  readonly pointerType: string;
  readonly gesture: Gesture;
  readonly startX: number;
  readonly startY: number;
  /** Wall minute under the pointer when it went down. */
  readonly anchorMin: number;
  readonly group: DisplayGroup | undefined;
  readonly edge: ResizeEdge | undefined;
  /** The block whose edge is resized, or the block that moves. */
  readonly member: LiveTimeBlock | undefined;
  /** `hlc` of every involved record at press: a change means an edit arrived from elsewhere. */
  readonly hlcs: ReadonlyMap<string, string>;
  /** Dragging is allowed (mouse: at once; touch: after the long press). */
  active: boolean;
  moved: boolean;
  /** Side of the resize anchor the handle is on; flips when the pointer crosses the anchor. */
  side: ResizeEdge | null;
  /** Day column the pointer sits in (week view, §2.8); null on the source day or in the day view. */
  crossDate: string | null;
  /** The dragged draft is held back from the day column under the pointer (§2.8). */
  crossDenied: boolean;
  timer: number | null;
  preview: LanePreview | null;
  /** Latest pointer position: auto-scroll re-snaps from it while the pointer stands still. */
  lastClientX: number;
  lastClientY: number;
}

export interface LaneCommands {
  /** Returns the write's `MarkOutcome` (2026-10b §2.5); quick mark consumes its "once" by it. */
  createBlock(date: string, span: Span, projectId: string): Promise<MarkOutcome>;
  updateBlockSpan(id: string, span: Span): Promise<void>;
  /** One transaction, one undo step: new member spans plus the members a group resize deletes. */
  resizeGroup(changes: readonly IdSpan[], deleteIds: readonly string[]): Promise<void>;
  /** Move a block or merged group to another personal date, one undo step (redesign §2.8). */
  moveBlocksToDate(ids: readonly string[], targetDate: string): Promise<void>;
}

/** Week-grid context of the cross-day drag (redesign §2.8); the day view passes none. */
export interface CrossDayGrid {
  /** Element containing the seven lane elements (`[data-testid="lane"][data-date]`). */
  readonly gridRef: { readonly current: HTMLElement | null };
  /** Visible blocks of a day column: the overlap tip over the target day reads them (§2.1.3). */
  readonly blocksOf?: (date: string) => readonly LiveTimeBlock[];
}

export interface LaneInteractionInput {
  readonly date: string;
  readonly laneRef: { readonly current: HTMLElement | null };
  readonly options: CreateOptions;
  /** Visible blocks of the date (their spans are the obstacles). */
  readonly blocks: readonly LiveTimeBlock[];
  readonly groups: readonly DisplayGroup[];
  /** Project stamped on click/drag in quick-mark mode (memory state, never a stored key); null in picker mode. */
  readonly quickProjectId: string | null;
  /** Key of the selected group: its handle pills win the edge hit test (§3.2.2). */
  readonly selectedKey: string | null;
  /**
   * The project picker or overlap popover is open: a press on empty lane space only dismisses
   * it — consumed, nothing created, selection kept (§3.2.1); a press on a block retargets it.
   */
  readonly overlayOpen: boolean;
  readonly enabled: boolean;
  readonly hoverEnabled: boolean;
  /** Present in the week view only: whole-block moves may switch the day column (§2.8). */
  readonly crossDay?: CrossDayGrid;
  readonly commands: LaneCommands;
  readonly onSelect: (groupKey: string | null) => void;
  readonly onCreateRequest: (span: Span, anchor: AnchorRect) => void;
  readonly onGroupClick: (group: DisplayGroup, anchor: AnchorRect) => void;
  /** Dismiss the open picker/popover (the consumed empty press of §3.2.1). */
  readonly onOverlayDismiss?: () => void;
  /** Quick-mark mode click on an existing block: add a parallel block beside it (design 2026-10-15 §1.2). */
  readonly onQuickAdd?: (group: DisplayGroup) => void;
  /** A quick-mark create finished: the caller consumes the "once" on `Created` (§4.3). */
  readonly onQuickMark?: (outcome: MarkOutcome) => void;
  /** The dragged record changed in the local store (sync or another tab): drag cancelled (§3.2). */
  readonly onRemoteCancel?: () => void;
  /** A gesture was committed: feeds the learning counters (§3.6.2). */
  readonly onGesture?: (kind: GestureKind) => void;
  /**
   * The mouse's secondary button (or an equivalent native `contextmenu`) hit a block: open the
   * block context menu at the pointer (2026-10b §6.3). Never called for touch, for a press
   * with a drag in progress, or for empty lane space (the browser's own menu stays there).
   */
  readonly onBlockContextMenu?: (group: DisplayGroup, point: { readonly x: number; readonly y: number }) => void;
}

export interface LaneHandlers {
  readonly onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onLostPointerCapture: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerLeave: () => void;
  readonly onContextMenu: (event: ReactMouseEvent<HTMLElement>) => void;
}

export interface LaneInteractions {
  readonly preview: LanePreview | null;
  /**
   * Pointer position of the drag in progress; null while idle. A mutable ref, not state: the
   * pointer moves many times per snapped unit and must not re-render the lane (the floating tip
   * follows it with its own listener).
   */
  readonly dragPointRef: { readonly current: DragPoint | null };
  /** The block a click would create at the mouse position (mouse only). */
  readonly hoverSpan: Span | null;
  /** A touch long press armed this gesture; the lane shows the armed visuals (§3.3). */
  readonly armed: ArmedKind | null;
  /** Group key the armed move belongs to. */
  readonly armedGroupKey: string | null;
  readonly handlers: LaneHandlers;
}

export function useLaneInteractions(input: LaneInteractionInput): LaneInteractions {
  const [preview, setPreview] = useState<LanePreview | null>(null);
  const dragPointRef = useRef<DragPoint | null>(null);
  const [hoverSpan, setHoverSpan] = useState<Span | null>(null);
  const [armed, setArmed] = useState<ArmedKind | null>(null);
  const [armedGroupKey, setArmedGroupKey] = useState<string | null>(null);
  const session = useRef<Session | null>(null);
  const scrollRaf = useRef<number | null>(null);
  /** Pointer type of the latest press: a native `contextmenu` event does not always carry one. */
  const lastPointerType = useRef('mouse');
  const latest = useRef(input);
  useEffect(() => {
    latest.current = input;
  });

  const laneElement = (): HTMLElement | null => latest.current.laneRef.current;

  /** The lane's measured box for the pure hit tests and previews (all zero before it is mounted). */
  function geometry(): LaneGeometry {
    const lane = laneElement();
    const { dayStartMin } = latest.current.options;
    if (lane === null) return { top: 0, height: 0, dayStartMin };
    const rect = lane.getBoundingClientRect();
    return { top: rect.top, height: rect.height, dayStartMin };
  }

  function minuteAt(clientY: number): number {
    return minuteAtY(geometry(), clientY);
  }

  /**
   * Anchor of a picker opened by the pointer: the point where the gesture ended, so the popover
   * opens right beside the pointer (left of it near the window's right edge) instead of beside
   * the whole lane, which in a wide lane puts it far from the click.
   */
  function rectAtPoint(clientX: number, clientY: number): AnchorRect {
    return { left: clientX, right: clientX, top: clientY, bottom: clientY };
  }

  const obstacles = (): Span[] => latest.current.blocks.map(spanOf);

  const pxPerMin = (): number => {
    const lane = laneElement();
    return lane === null ? 0 : lane.getBoundingClientRect().height / MINUTES_PER_DAY;
  };

  /**
   * Preview for the pointer at `currentMin` (lanePreview.ts); a single-block resize also moves
   * the handle's side of the anchor, which the session remembers for the flip.
   */
  function previewFor(s: Session, currentMin: number): LanePreview | null {
    const { options, blocks } = latest.current;
    const result = computePreview(s, currentMin, { options, blocks, pxPerMin: pxPerMin() });
    s.side = result.side;
    return result.preview;
  }

  /** Day columns of the week grid, left to right, for the cross-day hit test (§2.8). */
  function crossLanes(): ReadonlyArray<{ date: string; centerX: number }> {
    const grid = latest.current.crossDay?.gridRef.current ?? null;
    if (grid === null) return [];
    return Array.from(grid.querySelectorAll<HTMLElement>('[data-testid="lane"][data-date]'))
      .map((element) => {
        const rect = element.getBoundingClientRect();
        return { date: element.dataset['date'] ?? '', centerX: (rect.left + rect.right) / 2 };
      })
      .filter((lane) => lane.date !== '')
      .sort((a, b) => a.centerX - b.centerX);
  }

  /**
   * Week-grid column hit of a whole-block move (redesign §2.8): the pointer crossing the
   * centre line of another lane switches the target day to it; between two centre lines the
   * last target is kept (half a lane width of hysteresis each way). A draft never changes
   * days: a pointer past another lane's centre line only raises `crossDenied`.
   */
  function updateCrossTarget(s: Session): void {
    if (latest.current.crossDay === undefined || s.gesture !== Gesture.Block) return;
    const lanes = crossLanes();
    const source = latest.current.date;
    let index = lanes.findIndex((lane) => lane.date === (s.crossDate ?? source));
    if (index === -1) {
      s.crossDate = null;
      s.crossDenied = false;
      return;
    }
    while (index + 1 < lanes.length && s.lastClientX >= (lanes[index + 1]?.centerX ?? Infinity)) index += 1;
    while (index - 1 >= 0 && s.lastClientX <= (lanes[index - 1]?.centerX ?? -Infinity)) index -= 1;
    const hit = lanes[index]?.date ?? source;
    if (s.group?.status === BlockStatus.Draft) {
      // Drafts stay on their day: their id is derived from the date (§2.8).
      s.crossDenied = hit !== source;
      s.crossDate = null;
      return;
    }
    s.crossDenied = false;
    s.crossDate = hit === source ? null : hit;
  }

  function stopAutoScroll(): void {
    if (scrollRaf.current !== null) window.cancelAnimationFrame(scrollRaf.current);
    scrollRaf.current = null;
  }

  function stopSession(element: Element | null, s: Session): void {
    if (s.timer !== null) window.clearTimeout(s.timer);
    // Clear the session before releasing the capture: the lostpointercapture event this release
    // fires must read "no gesture in progress" and not cancel anything (it already ended).
    session.current = null;
    stopAutoScroll();
    setArmed(null);
    setArmedGroupKey(null);
    try {
      element?.releasePointerCapture(s.pointerId);
    } catch {
      // The pointer was not captured (touch long press never fired, or it was released already).
    }
  }

  function cancelSession(element: Element | null): void {
    const s = session.current;
    if (s === null) return;
    stopSession(element, s);
    setPreview(null);
    dragPointRef.current = null;
  }

  function capture(element: Element, pointerId: number): void {
    try {
      element.setPointerCapture(pointerId);
    } catch {
      // Capture is best effort; a lost capture ends the drag through pointercancel.
    }
  }

  /** Interruption events behave like Esc: the preview is discarded, never half-saved (§3.2). */
  useEffect(() => {
    const interrupt = (): void => cancelSession(laneElement());
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') interrupt();
    };
    window.addEventListener('blur', interrupt);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', interrupt);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Some record involved at press time has changed in the local store since (sync or another tab). */
  function recordsChangedSincePress(s: Session): boolean {
    if (s.hlcs.size === 0) return false;
    const byId = new Map(latest.current.blocks.map((block) => [block.id, block]));
    for (const [id, hlc] of s.hlcs) {
      const live = byId.get(id);
      if (live === undefined || live.hlc !== hlc) return true;
    }
    return false;
  }

  // A drag or resize in progress is cancelled the moment the pressed record changes in the
  // local store (sync arrival or another tab); the remote version stays (§3.2 general rules).
  // A press that has not crossed the 4px drag threshold is no drag yet: it is dropped quietly,
  // without the cancel toast — the stale press must still not act on the changed record.
  useEffect(() => {
    const s = session.current;
    if (s === null || s.hlcs.size === 0) return;
    const byId = new Map(input.blocks.map((block) => [block.id, block]));
    for (const [id, hlc] of s.hlcs) {
      const live = byId.get(id);
      if (live !== undefined && live.hlc === hlc) continue;
      const dragging = s.moved;
      cancelSession(laneElement());
      if (dragging) latest.current.onRemoteCancel?.();
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.blocks]);

  function handleClick(s: Session): void {
    const { options, quickProjectId, date, commands, onGroupClick, onCreateRequest, onQuickAdd } =
      latest.current;
    if (s.gesture !== Gesture.Empty) {
      const group = s.group;
      if (group === undefined) return;
      // Quick-mark mode: a plain click on an existing block (or merged group) adds a parallel
      // block of the chosen project beside it instead of opening the picker (design 2026-10-15 §1.2).
      if (quickProjectId !== null && onQuickAdd !== undefined) onQuickAdd(group);
      else onGroupClick(group, rectAtPoint(s.startX, s.startY));
      return;
    }
    const span = blockFromClick(s.anchorMin, options, obstacles());
    if (span === null) return;
    latest.current.onGesture?.(Gestures.CreateClick);
    if (quickProjectId !== null) {
      // The "once" is consumed only after the command returned successfully (§4.3).
      // A second rapid click while the first write is in flight is ignored (shared guard).
      if (!beginQuickMarkWrite()) return;
      void commands
        .createBlock(date, span, quickProjectId)
        .then((outcome) => latest.current.onQuickMark?.(outcome))
        .finally(endQuickMarkWrite);
    } else onCreateRequest(span, rectAtPoint(s.startX, s.startY));
  }

  /**
   * A group's key is its first member's id (laneModel): a resize that deletes that member
   * leaves `selectedKey` pointing at a group that no longer exists. The selection survives the
   * release (§3.2.1) by moving to the first surviving member, whose id keys the new group.
   */
  function reselectSurvivor(s: Session, result: GroupResizeResult): void {
    const group = s.group;
    if (group === undefined || !result.deleteIds.includes(group.key)) return;
    const changed = new Map(result.changes.map((change) => [change.id, change]));
    const survivor = group.members
      .filter((member) => !result.deleteIds.includes(member.id))
      .map((member) => ({
        id: member.id,
        startMin: changed.get(member.id)?.startMin ?? member.data.startMin,
      }))
      .sort((a, b) => a.startMin - b.startMin || (a.id < b.id ? -1 : 1))[0];
    if (survivor !== undefined) latest.current.onSelect(survivor.id);
  }

  async function commit(s: Session): Promise<void> {
    const { quickProjectId, date, commands, onCreateRequest, onGesture } = latest.current;
    const final = s.preview;
    if (final === null) {
      setPreview(null);
      return;
    }
    // Last line of defence against a race between the store update and the cancelling effect:
    // a record changed since the press is never overwritten — the remote version stays (§3.2).
    if (final.kind !== PreviewKind.Create && recordsChangedSincePress(s)) {
      setPreview(null);
      dragPointRef.current = null;
      latest.current.onRemoteCancel?.();
      return;
    }
    try {
      if (final.kind === PreviewKind.Create) {
        onGesture?.(Gestures.CreateDrag);
        if (quickProjectId !== null) {
          if (!beginQuickMarkWrite()) return;
          try {
            const outcome = await commands.createBlock(date, final.span, quickProjectId);
            latest.current.onQuickMark?.(outcome);
          } finally {
            endQuickMarkWrite();
          }
        } else onCreateRequest(final.span, rectAtPoint(s.lastClientX, s.lastClientY));
        return;
      }
      // Cross-day move (week view, §2.8): the whole group changes its personal date in one
      // transaction and one undo step; start, end and `tz` keep their values.
      if (final.kind === PreviewKind.Move && final.targetDate !== null) {
        const ids = s.group?.memberIds ?? [];
        if (ids.length > 0) {
          onGesture?.(Gestures.Move);
          await commands.moveBlocksToDate(ids, final.targetDate);
        }
        return;
      }
      if (final.group !== null) {
        if (final.group.changes.length > 0 || final.group.deleteIds.length > 0) {
          onGesture?.(final.kind === PreviewKind.Move ? Gestures.Move : Gestures.Resize);
          await commands.resizeGroup(final.group.changes, final.group.deleteIds);
          reselectSurvivor(s, final.group);
        }
        return;
      }
      const original = s.member;
      if (original !== undefined && final.blockId !== null && !sameSpan(final.span, spanOf(original))) {
        onGesture?.(final.kind === PreviewKind.Move ? Gestures.Move : Gestures.Resize);
        await commands.updateBlockSpan(final.blockId, final.span);
      }
    } finally {
      setPreview(null);
      dragPointRef.current = null;
    }
  }

  function updateHover(event: ReactPointerEvent<HTMLElement>): void {
    const { hoverEnabled, options } = latest.current;
    const target = event.target as Element;
    const hoverable =
      hoverEnabled && event.pointerType === 'mouse' && target.closest('[data-group-key]') === null;
    const next = hoverable ? blockFromClick(minuteAt(event.clientY), options, obstacles()) : null;
    setHoverSpan((previous) => (sameSpan(previous, next) ? previous : next));
  }

  function armSession(s: Session, element: Element): void {
    s.timer = null;
    s.active = true;
    capture(element, s.pointerId);
    try {
      navigator.vibrate?.(10);
    } catch {
      // Vibration is a progressive enhancement only.
    }
    if (s.gesture === Gesture.Empty) {
      // The armed preview shows the block a release would create (§3.3 TouchArmedCreate).
      const span = blockFromClick(s.anchorMin, latest.current.options, obstacles());
      if (span !== null) {
        const armedPreview = createPreview(span);
        s.preview = armedPreview;
        setPreview(armedPreview);
        dragPointRef.current = { x: s.lastClientX, y: s.lastClientY, touch: true };
      }
      setArmed(ArmedKind.Create);
      return;
    }
    setArmed(ArmedKind.Move);
    setArmedGroupKey(s.group?.key ?? null);
  }

  /**
   * Keeps the containers scrolling while the pointer rides their edges; re-snaps the preview.
   * Vertical everywhere; horizontal where a horizontal scroller exists (narrow week view, §3.7).
   */
  function ensureAutoScroll(s: Session): void {
    if (scrollRaf.current !== null) return;
    const vertical = scrollContainerOf(laneElement());
    // Horizontal auto-scroll belongs to the week grid's cross-day drag only (§2.8, §3.7): the
    // day view scrolls vertically, whatever horizontal scroller the page itself may have.
    const horizontal =
      latest.current.crossDay === undefined ? null : horizontalScrollContainerOf(laneElement());
    if (vertical === null && horizontal === null) return;
    const viewportRect = (
      container: HTMLElement,
    ): { top: number; bottom: number; left: number; right: number } =>
      container === document.scrollingElement
        ? { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth }
        : container.getBoundingClientRect();
    let lastTime = performance.now();
    const step = (time: number): void => {
      scrollRaf.current = null;
      const current = session.current;
      if (current !== s || !s.moved) return;
      const elapsedMs = time - lastTime;
      lastTime = time;
      let scrolled = false;
      if (vertical !== null) {
        const rect = viewportRect(vertical);
        const delta = scrollStepPx(edgeScrollVelocity(s.lastClientY, rect.top, rect.bottom), elapsedMs);
        if (delta !== 0) {
          const before = vertical.scrollTop;
          vertical.scrollTop = before + delta;
          scrolled = vertical.scrollTop !== before;
        }
      }
      if (horizontal !== null) {
        const rect = viewportRect(horizontal);
        const delta = scrollStepPx(edgeScrollVelocity(s.lastClientX, rect.left, rect.right), elapsedMs);
        if (delta !== 0) {
          const before = horizontal.scrollLeft;
          horizontal.scrollLeft = before + delta;
          scrolled = scrolled || horizontal.scrollLeft !== before;
        }
      }
      if (scrolled) {
        // The pointer stands still while the content moves: re-snap from the same position
        // (horizontal scrolling also shifts the day columns under the pointer, §2.8).
        updateCrossTarget(s);
        const next = previewFor(s, minuteAt(s.lastClientY));
        if (!samePreview(s.preview, next)) {
          s.preview = next;
          setPreview(next);
        }
      }
      scrollRaf.current = window.requestAnimationFrame(step);
    };
    scrollRaf.current = window.requestAnimationFrame(step);
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>): void => {
    const { enabled, groups, selectedKey, overlayOpen, onSelect, onOverlayDismiss } = latest.current;
    lastPointerType.current = event.pointerType;
    if (!enabled || !event.isPrimary) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // A new primary pointer means the previous gesture ended without a pointerup (e.g. outside the window).
    if (session.current !== null) cancelSession(event.currentTarget);
    const target = event.target as Element;
    if (target.closest('[data-ignore-lane]') !== null) return;

    const handle = target.closest<HTMLElement>('[data-edge]');
    const groupElement = target.closest<HTMLElement>('[data-group-key]');
    const group =
      groupElement === null
        ? undefined
        : groups.find((candidate) => candidate.key === groupElement.dataset['groupKey']);

    // A press on empty lane space (or the create band) while the picker or overlap popover is
    // open only dismisses it: consumed, nothing created, selection kept (§3.2.1). A press on a
    // block falls through and retargets the popover via the ordinary click path.
    if (overlayOpen && group === undefined) {
      // Consume the press entirely: preventing the default keeps the browser from focusing a
      // gap slot under the pointer, which would clear the selection the spec says to keep.
      event.preventDefault();
      onOverlayDismiss?.();
      return;
    }

    const touch = event.pointerType === 'touch';
    let edge =
      handle?.dataset['edge'] === ResizeEdge.Start || handle?.dataset['edge'] === ResizeEdge.End
        ? handle.dataset['edge']
        : undefined;
    if (edge !== undefined && group !== undefined && selectedKey !== group.key) {
      // An unselected block shorter than 18px (rendered, matching the `tiny` visuals) is a
      // whole-block move target everywhere (§3.2.2): its handle elements never start a resize —
      // that goes through the outer zones, or through the pills once the block is selected.
      if (cardHeightPx(geometry(), group) < MIN_TEXT_BLOCK_PX) edge = undefined;
    }
    if (group !== undefined && edge === undefined && !touch && event.pointerType === 'mouse') {
      // No pill under the pointer: classify by the inner edge bands (§3.2.2).
      edge = edgeFromBands(geometry(), event.clientY, group);
    }
    if (touch && edge !== undefined && group !== undefined && selectedKey !== group.key) {
      // Touch handles resize only once the block is selected (§3.3 TouchEdge).
      edge = undefined;
    }

    let outer: OuterResizeTarget | null = null;
    if (group === undefined && !touch) outer = outerResizeTarget(geometry(), groups, event.clientY);
    const targetGroup = group ?? outer?.group ?? undefined;
    const targetEdge = group !== undefined ? edge : outer?.edge;
    const gesture: Gesture =
      targetGroup === undefined ? Gesture.Empty : targetEdge !== undefined ? Gesture.Resize : Gesture.Block;
    const member =
      targetGroup === undefined
        ? undefined
        : targetEdge !== undefined
          ? memberForEdge(targetGroup, targetEdge)
          : targetGroup.members[0];

    onSelect(targetGroup?.key ?? null);
    const hlcs = new Map<string, string>();
    for (const item of targetGroup?.members ?? []) hlcs.set(item.id, item.hlc);
    const s: Session = {
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      gesture,
      startX: event.clientX,
      startY: event.clientY,
      anchorMin: minuteAt(event.clientY),
      group: targetGroup,
      edge: targetEdge,
      member,
      hlcs,
      active: !touch || gesture === Gesture.Resize,
      moved: false,
      side: null,
      crossDate: null,
      crossDenied: false,
      timer: null,
      preview: null,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
    };
    session.current = s;
    const element = event.currentTarget;
    if (s.active) capture(element, event.pointerId);
    else {
      s.timer = window.setTimeout(() => armSession(s, element), LONG_PRESS_MS);
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>): void => {
    const s = session.current;
    if (s === null) {
      updateHover(event);
      return;
    }
    if (event.pointerId !== s.pointerId) return;
    s.lastClientX = event.clientX;
    s.lastClientY = event.clientY;
    const distance = Math.hypot(event.clientX - s.startX, event.clientY - s.startY);
    if (!s.active) {
      if (distance > TOUCH_SLOP_PX) cancelSession(event.currentTarget);
      return;
    }
    if (!s.moved) {
      if (distance < DRAG_THRESHOLD_PX) return;
      s.moved = true;
    }
    dragPointRef.current = { x: event.clientX, y: event.clientY, touch: s.pointerType === 'touch' };
    updateCrossTarget(s);
    const next = previewFor(s, minuteAt(event.clientY));
    ensureAutoScroll(s);
    // The pointer moves many times per snapped unit: only a changed preview needs a render.
    if (samePreview(s.preview, next)) return;
    s.preview = next;
    setPreview(next);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>): void => {
    const s = session.current;
    if (s === null || event.pointerId !== s.pointerId) return;
    stopSession(event.currentTarget, s);
    if (!s.moved) {
      setPreview(null);
      dragPointRef.current = null;
      handleClick(s);
      return;
    }
    void commit(s);
  };

  const onPointerCancel = (event: ReactPointerEvent<HTMLElement>): void => cancelSession(event.currentTarget);

  // Losing the capture without our own release (e.g. the browser took the pointer) interrupts
  // the gesture; stopSession clears the session first, so the expected loss after a release or
  // commit finds no session and does nothing.
  const onLostPointerCapture = (event: ReactPointerEvent<HTMLElement>): void => {
    const s = session.current;
    if (s === null || event.pointerId !== s.pointerId) return;
    cancelSession(event.currentTarget);
  };

  // Esc cancels a drag in progress; a touch drag must not scroll the page.
  useEffect(() => {
    const lane = input.laneRef.current;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || session.current === null) return;
      // First step of the Esc priority order (§3.2.1): one Esc only cancels the drag. Capture
      // phase plus stopImmediatePropagation keeps every other Esc listener (picker popover,
      // sheet, deselection in useDayKeyboard) from also acting on the same key press,
      // independent of their registration order; preventDefault stops a native <dialog> cancel.
      event.preventDefault();
      event.stopImmediatePropagation();
      cancelSession(laneElement());
    };
    const onTouchMove = (event: TouchEvent): void => {
      const s = session.current;
      if (s !== null && s.active && s.pointerType === 'touch' && event.cancelable) event.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown, true);
    lane?.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      lane?.removeEventListener('touchmove', onTouchMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.laneRef]);

  return {
    preview,
    dragPointRef,
    hoverSpan,
    armed,
    armedGroupKey,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onLostPointerCapture,
      onPointerLeave: () => setHoverSpan(null),
      onContextMenu: (event) => {
        // A drag in progress swallows the menu (general rule of §3.2).
        if (session.current !== null) {
          event.preventDefault();
          return;
        }
        // Android Chrome fires `contextmenu` for the long press that arms a move: ignore it
        // and keep the browser's own menu away (2026-10b §6.3).
        const nativeType = (event.nativeEvent as { pointerType?: unknown }).pointerType;
        const pointerType =
          typeof nativeType === 'string' && nativeType !== '' ? nativeType : lastPointerType.current;
        if (pointerType === 'touch') {
          event.preventDefault();
          return;
        }
        const { enabled, groups, onBlockContextMenu } = latest.current;
        if (!enabled || onBlockContextMenu === undefined) return;
        const target = event.target as Element;
        if (target.closest('[data-ignore-lane]') !== null) return;
        const groupElement = target.closest<HTMLElement>('[data-group-key]');
        const group =
          groupElement === null
            ? undefined
            : groups.find((candidate) => candidate.key === groupElement.dataset['groupKey']);
        // Empty lane space and the create band keep the browser's native menu.
        if (group === undefined) return;
        event.preventDefault();
        onBlockContextMenu(group, { x: event.clientX, y: event.clientY });
      },
    },
  };
}
